import React from 'react';
import PropTypes from 'prop-types';
import Plot from 'react-plotly.js';
import { GridLoader } from 'react-spinners';
import { connect } from 'react-redux';

import './plotModal.scss';

// Conversion factors from meters (base unit) to other pressure units
const PRESSURE_CONVERSION = {
  meter: { factor: 1, label: 'Meters' },
  bar: { factor: 0.09804139432, label: 'Bar' },
  kpa: { factor: 9.80665, label: 'kPa' },
  psi: { factor: 1.42233, label: 'PSI' },
};

class SensorPressureGraph extends React.Component {

  static get propTypes() {
    return ({
      title: PropTypes.string,
      selectedFeature: PropTypes.number,
      plotData: PropTypes.array,
      timeZone: PropTypes.string,
      uiInfo: PropTypes.object.isRequired,
      onPlotValueClick: PropTypes.func,
      isFetching: PropTypes.bool,
      allowChangeUnits: PropTypes.bool,
      rangemode: PropTypes.oneOf(['normal', 'tozero']),
      compactLayout: PropTypes.bool,
    });
  }

  constructor(props) {
    super(props);

    const isCompact = props.compactLayout;
    this.state = {
      selectedFeature: -1,
      activePage: 1,
      layout: {
        showlegend: this.props.legend !== null ? this.props.legend : true,
        type: 'date',
        margin: isCompact
          ? { t: 10, b: 70, l: 50, r: 20 }
          : { t: 80, b: 50, l: 60, r: 30 },
        legend: isCompact
          ? { orientation: 'h', xanchor: 'center', x: 0.5, yanchor: 'top', y: -0.18, font: { size: 11 } }
          : undefined,
        yaxis: {
          rangemode: props.rangemode || 'tozero',
          autorange: true
        }
      },
      config: {
        displaylogo: false,
        responsive: true,
        displayModeBar: true,
        modeBarButtonsToRemove: [
          'drawrect',
          'zoomIn2d',
          'zoomOut2d',
          'resetScale2d',
          'toggleSpikelines',
          'resetViewMapbox',
        ],
      },
    };
  }

  componentDidMount() {
    const { uiInfo, title } = this.props;

    if (uiInfo) {
      const pWidth = 0.38 * (uiInfo.pageWidth - 10);
      const pHeight = 0.3 * uiInfo.pageHeight;

      const newLayout = {
        ...this.state.layout,
        width: pWidth,
        height: pHeight,
        title
      };

      this.setState({ layout: newLayout });
    }
  }

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.title !== this.props.title) {
      const newLayout = {
        ...this.state.layout,
        title: nextProps.title
      };

      this.setState({ layout: newLayout });
    }
  }

  render() {
    const { plotData, isFetching, onPlotValueClick, pressureUnit } = this.props;
    const { layout, config } = this.state;

    // Get conversion factor based on global pressure unit
    const conversion = PRESSURE_CONVERSION[pressureUnit] || PRESSURE_CONVERSION.meter;

    let data = [];
    if (plotData) {
      data = [
        ...plotData.map((traceData) => ({
          ...traceData,
          y: traceData.y.map((y) => (y * conversion.factor).toFixed(2))
        }))
      ];
    }

    // Enforce minimum Y-axis range of 3 Bar (converted to current unit)
    const MIN_RANGE_BAR = 3;
    const minRangeInUnit = MIN_RANGE_BAR * (conversion.factor / PRESSURE_CONVERSION.bar.factor);
    let yaxisOverride = {};
    if (data.length > 0) {
      const allY = data.flatMap((trace) => trace.y.map(Number).filter((v) => !isNaN(v)));
      if (allY.length > 0) {
        const yMin = Math.min(...allY);
        const yMax = Math.max(...allY);
        const currentRange = yMax - yMin;
        if (currentRange < minRangeInUnit) {
          const center = (yMin + yMax) / 2;
          const halfRange = minRangeInUnit / 2;
          const lower = Math.floor(center - halfRange);
          const upper = Math.max(
            lower + Math.ceil(minRangeInUnit),
            Math.ceil(center + halfRange)
          );
          yaxisOverride = {
            range: [lower, upper],
            autorange: false,
          };
        }
      }
    }

    const finalLayout = {
      ...layout,
      yaxis: {
        ...layout.yaxis,
        ...yaxisOverride,
      },
    };

    return (
      <div>
        {isFetching &&
          <div style={{ paddingLeft: '35%', paddingTop: '5%' }}>
            <GridLoader
              sizeUnit={"px"}
              size={60}
              margin='10px'
              color={'#123abc'}
              loading={isFetching}
            />
          </div>
        }

        {!isFetching &&
          <div>
            <Plot
              data={data}
              layout={finalLayout}
              config={config}
              onClick={onPlotValueClick}
            />
          </div>
        }
      </div>
    );
  }
}

SensorPressureGraph.contextTypes = {
  t: PropTypes.func.isRequired
};

export default connect((state) => ({
  pressureUnit: state.local.pressureUnit,
}))(SensorPressureGraph);
